import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Color palette for different tasks
const COLOR_PALETTE = [
  'rgba(75, 192, 192, 0.8)',
  'rgba(255, 99, 132, 0.8)',
  'rgba(54, 162, 235, 0.8)',
  'rgba(255, 206, 86, 0.8)',
  'rgba(153, 102, 255, 0.8)',
  'rgba(255, 159, 64, 0.8)',
  'rgba(199, 199, 199, 0.8)',
  'rgba(83, 217, 217, 0.8)',
  'rgba(0, 43, 73, 0.8)',
  'rgba(0, 103, 160, 0.8)',
  'rgba(46, 204, 113, 0.8)',
  'rgba(231, 76, 60, 0.8)',
  'rgba(241, 196, 15, 0.8)',
  'rgba(155, 89, 182, 0.8)',
  'rgba(52, 152, 219, 0.8)',
  'rgba(230, 126, 34, 0.8)',
];

const formatHoursLabel = (value = 0) => {
  const hours = Math.floor(value);
  const minutes = Math.round((value - hours) * 60);
  return `${hours}h ${minutes}m`;
};

export function ReviewCalendarViewChart({ groupedData = {}, onBarClick, viewMode = 'per-day' }) {
  const processedData = useMemo(() => {
    if (!groupedData || typeof groupedData !== 'object' || Object.keys(groupedData).length === 0) {
      return {
        labels: [],
        perDayDatasets: [],
        cumulativeDatasets: [],
      };
    }

    const sortedEntries = Object.entries(groupedData)
      .sort(([a], [b]) =>
        new Date(a.split('/').reverse().join('-')) - new Date(b.split('/').reverse().join('-'))
      );

    const labels = sortedEntries.map(([date]) => date);

    const allTasks = new Set();
    sortedEntries.forEach(([, dateData]) => {
      if (dateData && typeof dateData === 'object') {
        Object.keys(dateData).forEach(task => allTasks.add(task));
      }
    });

    const tasksList = Array.from(allTasks).sort();

    const perDayDatasets = tasksList.map((task, index) => {
      const data = sortedEntries.map(([, dateData]) => {
        const taskDuration = dateData?.[task] || 0;
        return taskDuration / 60;
      });

      return {
        label: task,
        data,
        backgroundColor: COLOR_PALETTE[index % COLOR_PALETTE.length],
        borderColor: COLOR_PALETTE[index % COLOR_PALETTE.length].replace('0.8', '1'),
        borderWidth: 1,
        stack: 'per-day',
      };
    });

    const cumulativeTaskDatasets = tasksList.map((task, index) => {
      let runningTotal = 0;
      const data = sortedEntries.map(([, dateData]) => {
        const taskDuration = dateData?.[task] || 0;
        runningTotal += taskDuration / 60;
        return runningTotal;
      });

      const color = COLOR_PALETTE[index % COLOR_PALETTE.length].replace('0.8', '1');

      return {
        label: task,
        data,
        borderColor: color,
        backgroundColor: 'transparent',
        pointBackgroundColor: color,
        pointRadius: 3,
        borderWidth: 2,
        tension: 0.3,
      };
    });

    let runningTotal = 0;
    const totalDataset = {
      label: 'Total',
      data: sortedEntries.map(([, dateData]) => {
        const totalMinutesForDay = dateData
          ? Object.values(dateData).reduce((sum, minutes) => sum + minutes, 0)
          : 0;
        runningTotal += totalMinutesForDay / 60;
        return runningTotal;
      }),
      borderColor: '#ffffff',
      backgroundColor: 'transparent',
      pointBackgroundColor: '#ffffff',
      pointRadius: 4,
      borderWidth: 3,
      tension: 0.2,
      borderDash: [6, 4],
    };

    return {
      labels,
      perDayDatasets,
      cumulativeDatasets: [totalDataset, ...cumulativeTaskDatasets],
    };
  }, [groupedData]);

  const handleChartClick = (event, elements) => {
    if (elements.length > 0 && onBarClick) {
      const elementIndex = elements[0].index;
      const clickedDate = processedData.labels[elementIndex];
      onBarClick(clickedDate);
    }
  };

  const perDayOptions = {
    responsive: true,
    maintainAspectRatio: true,
    onClick: handleChartClick,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: 'white',
          font: {
            size: 12,
            family: 'Product Sans, sans-serif',
          },
          padding: 15,
          usePointStyle: true,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${formatHoursLabel(value)}`;
          },
          footer: function (tooltipItems) {
            const total = tooltipItems.reduce((sum, item) => sum + item.parsed.y, 0);
            return `Total: ${formatHoursLabel(total)}`;
          },
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        footerColor: 'rgba(255, 255, 255, 0.7)',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          color: 'white',
          font: {
            size: 11,
          },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
      y: {
        stacked: true,
        ticks: {
          color: 'white',
          callback: function(value) {
            return value + 'h';
          },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        title: {
          display: true,
          text: 'Hours',
          color: 'white',
          font: {
            size: 13,
            weight: 'bold',
          },
        },
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  };

  const cumulativeOptions = {
    responsive: true,
    maintainAspectRatio: true,
    onClick: handleChartClick,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: 'white',
          font: {
            size: 12,
            family: 'Product Sans, sans-serif',
          },
          padding: 15,
          usePointStyle: true,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${formatHoursLabel(value)}`;
          },
        },
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        ticks: {
          color: 'white',
          font: {
            size: 11,
          },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
      y: {
        ticks: {
          color: 'white',
          callback: function(value) {
            return value + 'h';
          },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        title: {
          display: true,
          text: 'Cumulative Hours',
          color: 'white',
          font: {
            size: 13,
            weight: 'bold',
          },
        },
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  };

  const noDataAvailable = processedData.labels.length === 0;
  if (noDataAvailable) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.7)' }}>
        No analytics data available for the selected filters.
      </div>
    );
  }

  const chartData =
    viewMode === 'cumulative'
      ? { labels: processedData.labels, datasets: processedData.cumulativeDatasets }
      : { labels: processedData.labels, datasets: processedData.perDayDatasets };

  const chartOptions = viewMode === 'cumulative' ? cumulativeOptions : perDayOptions;
  const ChartRenderer = viewMode === 'cumulative' ? Line : Bar;

  return <ChartRenderer data={chartData} options={chartOptions} />;
}
